import React from 'react';
import { Box, SxProps, Theme } from '@mui/material';

export interface ColorSwatchProps {
  /**
   * The color value to display (hex, rgb, rgba, etc.)
   */
  color: string;
  /**
   * The name/label of the color
   */
  name?: string;
  /**
   * Size of the swatch
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Whether to show the color value as text
   * @default false
   */
  showValue?: boolean;
  /**
   * Custom styles to apply
   */
  sx?: SxProps<Theme>;
}

/**
 * ColorSwatch - Display a color sample with optional label
 * 
 * Use cases:
 * - Design system documentation
 * - Color pickers
 * - Theme selectors
 * - Style guides
 * 
 * @example
 * ```tsx
 * <ColorSwatch color="#6366f1" name="Primary" />
 * <ColorSwatch color="rgba(255, 255, 255, 0.1)" showValue />
 * ```
 */
export const ColorSwatch: React.FC<ColorSwatchProps> = ({ 
  color,
  name,
  size = 'medium',
  showValue = false,
  sx = {},
}) => {
  const sizeMap = {
    small: 40,
    medium: 60,
    large: 80,
  };

  return (
    <Box
      sx={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        ...sx,
      }}
    >
      <Box
        sx={{
          width: sizeMap[size],
          height: sizeMap[size],
          backgroundColor: color,
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
        }}
      />
      {name && (
        <Box
          sx={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.9)',
            textAlign: 'center',
          }}
        >
          {name}
        </Box>
      )}
      {showValue && (
        <Box
          sx={{
            fontSize: '0.7rem',
            fontFamily: 'monospace',
            color: 'rgba(255, 255, 255, 0.6)',
            textAlign: 'center',
          }}
        >
          {color}
        </Box>
      )}
    </Box>
  );
};
